/**
 * DEFUSE S15 Slice E — GENERIC side-effect routing infrastructure (the PIVOT).
 *
 * A typed property descriptor whose write is NOT a plain field set carries
 * `constraints.sideEffects = "<mutatorName>"` (descriptors/types.ts). The writer
 * core (`applyTypedPropertyEditWithDescriptor`) SURFACES such a cell as
 * `status: "side-effect"` with a `TypedPropertySideEffect` directive instead of
 * patching `typedFieldRef.path` directly — because the mutator must rebuild the
 * COUPLED fields (HATCH Pattern → solidFill / gradient / customPatternLines) that
 * a single-field write would leave stale.
 *
 * This module is the OTHER half of that contract: a NAME → pure mutator REGISTRY
 * (`SIDE_EFFECT_MUTATOR_REGISTRY`) plus the generic consumer `applyTypedSideEffect`
 * the geometryReducer routes surfaced directives through. It is GENERIC by design
 * — the same choke-point unblocks the later D/F/G side-effect arms (Viewport view
 * / scale / lock, Block Hyperlink): each lands as one registry entry, never a
 * bespoke reducer branch.
 *
 * LOUD FAIL (by construction): `applyTypedSideEffect` THROWS for an unregistered
 * mutator name — a future side-effect field can NEVER silently no-op the way the
 * pre-PIVOT `buildTypedFieldPatch` dropped every non-`applied` result. The census
 * (`sideEffectMutators.census.test.ts`) additionally proves every DECLARED
 * `constraints.sideEffects` name is either registered here OR belongs only to a
 * kind that is not yet a cutover kind (so it is never routed) — the
 * httpErrorsTaxonomyGate-style completeness gate.
 *
 * ПРИОРИТЕТ #1: the coupled-field CAD semantics (pattern ⇒ solidFill derivation,
 * gradient / custom pattern-line clearing) live HERE in the package-level CAD core
 * as `applyHatchPatternMutation`, reused by BOTH the palette side-effect Step AND
 * the `geometry/hatchedit` command (geometryReducer) — one truth, no hand-rolled
 * reconcile.
 */

import {
  fitPointsToControlPoints,
  type ArrayRectEntity,
  type DrawingEntity,
  type HatchEntity,
  type HatchGradient,
  type HatchPatternLine,
  type SplineEntity,
  type SplinePoint,
  type TextEntity,
  type ViewportEntity,
} from "@dxf-ai-editor/drawing-model";

import {
  arrayAxisAngleDisplayDegToRaw,
  resolveArrayAxisAngleDisplayDeg,
} from "./descriptors/containers";
import type { TypedPropertySideEffect } from "./typedPropertyWriterCore";
import {
  parseViewportScaleFraction,
  setViewportLockEntity,
  setViewportScaleEntity,
  setViewportViewEntity,
  viewportStoredScaleFraction,
} from "./viewportMutateEntity";

/**
 * The coupled-field HATCH pattern mutation — the ONE TRUTH for "a pattern / solid
 * / gradient change rebuilds the dependent fields", replacing the deleted
 * `propertyTranslator` HATCH branch derivation AND the hand-rolled
 * `geometry/hatchedit` rawPatch reconcile.
 *
 *   - `patternName` (non-empty) → set it; on a pattern-CLASS change (name differs
 *     from the stored one, trimmed) DERIVE `solidFill` (AutoCAD's "SOLID" pattern
 *     IS the solid fill — DXF group 70) and CLEAR the stale `gradient` (450-block)
 *     + `customPatternLines` (78-block), which the renderer / PDF-DXF export would
 *     otherwise let WIN over the new pattern.
 *   - `solidFill` (explicit) → OVERRIDE the derived value. Used by a direct Solid
 *     toggle and by the `hatchedit` UNDO inverse, which restores the PRIOR
 *     solidFill VERBATIM so an inconsistent imported hatch (solidFill not matching
 *     its patternName's SOLID-ness) round-trips.
 *   - `gradient` / `customPatternLines` PRESENT (the KEY exists) → OVERRIDE:
 *     `null`/`undefined` restores-to-absent (clear), a real payload restores it
 *     VERBATIM (undo). ABSENT (no key) → leave whatever the pattern-name derivation
 *     decided. This is the exact `"gradient" in command` semantics the deleted
 *     hatchedit rawPatch used.
 */
export type HatchPatternMutation = {
  patternName?: string;
  solidFill?: boolean;
  gradient?: HatchGradient | null;
  customPatternLines?: HatchPatternLine[] | null;
};

/**
 * Apply {@link HatchPatternMutation} to a HATCH entity, PRESERVING referential
 * identity when nothing actually changes (so a full-row re-submit of the
 * unchanged Pattern is a no-op that keeps the stored gradient / precision). Pure —
 * never mutates `entity`.
 */
export function applyHatchPatternMutation(
  entity: HatchEntity,
  m: HatchPatternMutation,
): HatchEntity {
  const patch: Record<string, unknown> = {};

  // 1. Pattern name (+ derived coupling) — mirrors the deleted translator
  //    `case "Pattern"`: verbatim name (reject only empty), and ONLY on a real
  //    name change flip solidFill + clear gradient / custom lines.
  if (m.patternName !== undefined && m.patternName.trim() !== "") {
    patch.patternName = m.patternName;
    const nameChanged = m.patternName.trim() !== entity.patternName.trim();
    if (nameChanged) {
      patch.solidFill = m.patternName.trim().toUpperCase() === "SOLID";
      patch.gradient = undefined;
      patch.customPatternLines = undefined;
    }
  }

  // 2. Explicit overrides — the hatchedit UNDO inverse restoring the PRIOR
  //    coupled fields VERBATIM. CONTRACT (@codex P2-1, undefined-vs-null): a
  //    field is UNTOUCHED unless the caller passes a NON-`undefined` value; only
  //    `null` is the explicit restore-to-absent (clear), a real payload restores
  //    it. This distinguishes an OMITTED field (a typed caller spreading
  //    `gradient: undefined` while editing an UNRELATED field must NOT clear the
  //    stored gradient) from an explicit `null` clear — matching the pre-slice
  //    reducer, which ignored present-but-`undefined`.
  if (m.solidFill !== undefined) patch.solidFill = m.solidFill;
  if (m.gradient !== undefined) {
    patch.gradient = m.gradient === null ? undefined : m.gradient;
  }
  if (m.customPatternLines !== undefined) {
    patch.customPatternLines =
      m.customPatternLines === null ? undefined : m.customPatternLines;
  }

  let changed = false;
  const next = { ...entity } as Record<string, unknown>;
  for (const key of Object.keys(patch)) {
    if (!Object.is((entity as Record<string, unknown>)[key], patch[key])) {
      next[key] = patch[key];
      changed = true;
    }
  }
  return changed ? (next as unknown as HatchEntity) : entity;
}

/**
 * Cross-field CONTEXT a coupled-field mutator gates its dependent-field REBUILD
 * on — the labels of the OTHER entity-specific changes in the SAME apply whose
 * write would GENUINELY change the pre-mutation document (a real plain edit, not
 * a no-op / reject re-submit of an unchanged full-bag row). The single-directive
 * `sideEffect` alone cannot express "did the user ALSO edit a sibling", so this
 * carries the writer's own CHANGED-verdict for every sibling.
 *
 * `splineFitRecomputeMutator` reads it to SUPPRESS the fit→control recompute when
 * `Control points` / `Knots` were also genuinely edited (the explicit control
 * polygon wins — the legacy `!cpChange && !knotsChange` gate), and to skip the
 * `weights`/`rational` reset when those were explicitly edited (an undo restore
 * carries them). PRESENCE would be wrong: a single-select apply re-dispatches the
 * FULL bag (every unchanged row present), so gating on "Control points is in the
 * change set" would suppress the recompute on EVERY panel fit-point edit.
 * `hatchPatternMutator` ignores it (its coupling is intra-directive).
 */
export type SideEffectMutatorContext = {
  changedSiblingLabels: ReadonlySet<string>;
};

/** Shared immutable empty context for callers with no sibling change set (the
 *  census / a direct single-directive apply). */
const EMPTY_SIDE_EFFECT_CONTEXT: SideEffectMutatorContext = {
  changedSiblingLabels: new Set<string>(),
};

/**
 * A registered side-effect mutator: consumes ONE writer-surfaced directive (a
 * single label's parsed value) plus the cross-field {@link SideEffectMutatorContext}
 * and returns the entity with the coupled fields rebuilt. Pure — `sideEffect.raw`
 * is the already-parsed value, `sideEffect.label` the descriptor label.
 */
export type SideEffectMutator = (
  entity: DrawingEntity,
  sideEffect: TypedPropertySideEffect,
  context: SideEffectMutatorContext,
) => DrawingEntity;

/**
 * `hatchPatternMutator` — the registry ADAPTER that maps a Hatch `Pattern`
 * directive onto {@link applyHatchPatternMutation}. Only `Pattern` surfaces as a
 * side-effect directive: `Solid` is a DERIVED READ-ONLY display (containers.ts —
 * @codex P2 Solid cluster), so it never routes here. Guards a non-Hatch entity
 * (defensive: only the Hatch `Pattern` descriptor declares this name). Ignores
 * the sibling `context` — its coupling is entirely intra-directive.
 */
const hatchPatternMutator: SideEffectMutator = (entity, sideEffect) => {
  if (entity.type !== "Hatch") return entity;
  if (sideEffect.label === "Pattern") {
    const name =
      typeof sideEffect.raw === "string" ? sideEffect.raw : sideEffect.display;
    return applyHatchPatternMutation(entity as HatchEntity, { patternName: name });
  }
  return entity;
};

/**
 * `splineFitRecomputeMutator` (S15 Slice D) — a Spline `Fit points` directive
 * REBUILDS the control polygon. THE COUPLING (why this is a side-effect, not a
 * plain `fitPoints` write): the RENDERED curve is built from `controlPoints`
 * (`splineToRenderable`), NOT `fitPoints`, so a naive plain write of `fitPoints`
 * would leave the curve on the OLD control polygon (#317). This reproduces the
 * DELETED legacy `propertyTranslator` Spline fit block VERBATIM:
 *   - set `fitPoints` to the (merged, count-unlocked) parsed value;
 *   - for a NON-EMPTY list AND when neither `Control points` NOR `Knots` was
 *     genuinely edited in the same apply (the `!cpChange && !knotsChange` gate),
 *     recompute `controlPoints` + the clamped knot vector via the one-truth
 *     `fitPointsToControlPoints` (honouring the stored start/end tangent + degree)
 *     and RESET `weights: []` + `rational: false` (a fit-defined spline is a
 *     NON-rational interpolating B-spline — DXF group 70 bit 4 clear, per the
 *     AutoCAD DXF reference + ezdxf). The reset is SKIPPED for a field the user
 *     ALSO edited (legacy applied the explicit Weights/Rational restore AFTER the
 *     recompute block, so the explicit value — incl. an undo restore — wins).
 * Guards a non-Spline entity / non-`Fit points` label (defensive). Pure.
 */
const splineFitRecomputeMutator: SideEffectMutator = (entity, sideEffect, context) => {
  if (entity.type !== "Spline") return entity;
  if (sideEffect.label !== "Fit points") return entity;
  const rawPts = sideEffect.raw;
  // a malformed fit-point list (`toRaw` reject sentinel) → NO-OP, mirroring the
  // legacy `if (pts)` guard — never corrupt the entity with a bogus set.
  if (!Array.isArray(rawPts)) return entity;
  const spline = entity as SplineEntity;
  // 3-D points (z ALWAYS present — the `fitPoints` schema is drawingPoint3DSchema)
  // so the array is assignable AND `fitPointsToControlPoints` (SplinePoint[]) can
  // consume it. `rawPts` is the descriptor `toRaw` output (merged, count-unlocked).
  const fitPoints: Array<{ x: number; y: number; z: number }> = rawPts.map((p) => {
    const rec = p as { x: number; y: number; z?: number };
    return { x: rec.x, y: rec.y, z: rec.z ?? 0 };
  });
  let next: SplineEntity = { ...spline, fitPoints };

  const changed = context.changedSiblingLabels;
  // Codex P2 (#317) — recompute ONLY when a non-empty fit list is set AND the
  // control polygon / knots were NOT explicitly edited (else they win).
  if (
    fitPoints.length > 0 &&
    !changed.has("Control points") &&
    !changed.has("Knots")
  ) {
    const startTangent: SplinePoint | undefined = spline.startTangent
      ? { x: spline.startTangent.x, y: spline.startTangent.y, z: spline.startTangent.z }
      : undefined;
    const endTangent: SplinePoint | undefined = spline.endTangent
      ? { x: spline.endTangent.x, y: spline.endTangent.y, z: spline.endTangent.z }
      : undefined;
    const degree = Number.isFinite(spline.degree) ? spline.degree : 3;
    const { controlPoints, knots } = fitPointsToControlPoints(
      fitPoints,
      startTangent,
      endTangent,
      degree,
    );
    const recomputed: Partial<SplineEntity> = {
      controlPoints: controlPoints.map((p) => ({ x: p.x, y: p.y, z: p.z ?? 0 })),
      knots,
    };
    // reset weights/rational UNLESS the user explicitly edited them (undo restore).
    if (!changed.has("Weights")) recomputed.weights = [];
    if (!changed.has("Rational")) recomputed.rational = false;
    next = { ...next, ...recomputed };
  }
  return next;
};

/**
 * `viewportScaleMutator` (S15 Slice F) — a Viewport `Scale` directive (the "1:100"
 * label) rebuilds the COUPLED view state via the shared `setViewportScaleEntity`:
 * `viewHeight = paperHeight × (denom/num)`, the `num:denom` cannoscale name +
 * factor, the authoritative `vpOverrideScale` typed field, AND the VP_OVERRIDES
 * XDATA scale codes. THE COUPLING (why this is a side-effect, not a plain
 * `vpOverrideScale` write): the scale is DERIVED (`height/viewHeight`), so a plain
 * write of a scale field would leave `viewHeight` (the actual rendered view) stale
 * and desync the two XDATA/typed scale carriers. This is BYTE-IDENTICAL to the
 * dedicated `applyViewportSetScale` command (they call the SAME entity helper — one
 * truth). The num:denom is parsed from the raw label (`sideEffect.display`), NOT the
 * ratio `raw` (which the descriptor `toRaw` lossily produces): only the label
 * carries both parts the cannoscale name + `vpOverrideScale` need. An unparseable
 * label (or a non-Viewport entity) → NO-OP (return the entity unchanged).
 */
const viewportScaleMutator: SideEffectMutator = (entity, sideEffect) => {
  if (entity.type !== "Viewport") return entity;
  if (sideEffect.label !== "Scale") return entity;
  const fraction = parseViewportScaleFraction(sideEffect.display);
  if (!fraction) return entity;
  return setViewportScaleEntity(entity as ViewportEntity, fraction.num, fraction.denom);
};

/**
 * `viewportMutator` (S15 Slice F) — the four COUPLED viewport view/lock directives.
 * Dispatches on the descriptor label onto the shared entity helpers so a panel edit
 * reproduces the dedicated command EXACTLY (`applyViewportSetLock` /
 * `applyViewportSetView` call the same helpers — one truth):
 *   - `Display lock` → `setViewportLockEntity` (displayLocked + VP_OVERRIDES XDATA
 *     lock flag + group-90 0x4000 status bit, kept in sync so the lock survives
 *     DXF round-trip — a plain `displayLocked` write would leave both stale);
 *   - `View height`  → `setViewportViewEntity({ viewHeight })` (group 45 — a ZOOM;
 *     re-derives the displayed scale so the status-bar selector reflects it);
 *   - `View center X/Y` → `setViewportViewEntity({ viewCenter })` (group 12/22 — a
 *     PAN; the sibling axis is merged from the RUNNING entity so a same-apply X+Y
 *     edit composes).
 * The numeric directives (`raw` = `Number(display)`, no descriptor `toRaw`) are
 * side-effect-surfaced UNCONDITIONALLY (the writer core skips its numeric-constraint
 * gate for side-effects), so an empty / non-finite value is GUARDED here — a blank
 * or NaN view field is a NO-OP (AutoCAD revert-on-empty). VALIDATION LIFT: `View
 * height` (group 45) is the zoom magnification and MUST be STRICTLY POSITIVE — the
 * `geometry/viewport-set-view` command enforces `viewHeight: z.number().positive()`
 * (editor.ts) before `applyViewportSetView`, but the panel path bypasses that
 * schema, so a `0`/`-50` view height is a NO-OP here too (never a degenerate
 * `viewHeight ≤ 0` the mutator's own comment forbids). `View center X/Y` are PAN
 * coordinates that legitimately accept `0` / negative — they are NOT constrained.
 * A non-Viewport / unknown label → NO-OP.
 */
const viewportMutator: SideEffectMutator = (entity, sideEffect) => {
  if (entity.type !== "Viewport") return entity;
  const vp = entity as ViewportEntity;
  if (sideEffect.label === "Display lock") {
    return setViewportLockEntity(vp, sideEffect.raw === true);
  }
  // The numeric view fields validate through the ONE `viewportViewValueRejects`
  // truth the shell-mirror also consults (@codex P2-3) — a blank / non-finite
  // parse, and a non-positive `View height`, are NO-OPs here AND shell-skipped, so
  // the preview never shows a value the mutator refuses.
  if (viewportViewValueRejects(sideEffect.label, sideEffect.display)) return entity;
  const n = Number(sideEffect.display.trim());
  switch (sideEffect.label) {
    case "View height":
      return setViewportViewEntity(vp, { viewHeight: n });
    case "View center X":
      return setViewportViewEntity(vp, { viewCenter: { x: n, y: vp.viewCenter.y } });
    case "View center Y":
      return setViewportViewEntity(vp, { viewCenter: { x: vp.viewCenter.x, y: n } });
    default:
      return entity;
  }
};

/**
 * `arrayAssociativeMutator` — a Rectangular Associative Array `Associative`
 * directive REBUILDS the coupled `axisAngle` (Codex P2, local review rounds
 * 5 + 9 on the array-spec-render-arm slice). THE COUPLING (why this is a
 * side-effect, not a plain `associative` write): `axisAngle`'s STORAGE unit
 * AND its neutral-baseline offset both depend on `associative` — RADIANS
 * with a π/2 (90°) neutral baseline (the angle BETWEEN the row/column axes,
 * π/2 for a standard orthogonal array) for an associative array's resolved
 * AcDbAssocArrayRectangularParameters "AxesAngle"; DEGREES with a 0°
 * neutral baseline straight off DXF group 50 for a non-associative array
 * (see containers.ts `resolveArrayAxisAngleDisplayDeg`'s doc for the full
 * derivation). A plain `associative` field write would flip BOTH the unit
 * AND the baseline every downstream reader interprets `axisAngle` through,
 * WITHOUT converting the stored NUMBER — silently corrupting the
 * user-facing rotation (a standard imported associative array, axisAngle=
 * π/2, toggled to non-associative would display "1.5708°" — round 5's own
 * gap — or, post round-9 baseline-fix, an UNCONVERTED "0°" would silently
 * become the RAW-radians "0" on the non-associative side, i.e. group-50
 * "0°", which happens to coincide for THIS specific value but would NOT
 * for any other displayed angle).
 *
 * Converts `axisAngle` by ROUND-TRIPPING through the SAME
 * `resolveArrayAxisAngleDisplayDeg` / `arrayAxisAngleDisplayDegToRaw` pair
 * the descriptor's own `display`/`toRaw` use (one truth — the unit AND
 * baseline conversion can never drift out of sync across the three
 * call sites): read the CURRENT raw as a display value under the OLD
 * associative flag, then write that SAME display value back as a raw under
 * the NEW flag. This keeps the DISPLAYED/real-world angle invariant across
 * the toggle, matching AutoCAD (toggling an array's associativity does not
 * spin its rows/columns) — e.g. a standard array (associative, raw=π/2,
 * display=0°) toggled to non-associative lands at raw=0° (display 0°,
 * unchanged), not the pre-round-9 raw=90°. A same-value re-submit
 * (`nextAssociative === entity.associative`) is a pure no-op — `axisAngle`
 * is left untouched (no unnecessary floating-point round-trip on every
 * unrelated re-save). Guards a non-ArrayRect entity / non-`Associative`
 * label (defensive: only the ArrayRect `Associative` descriptor declares
 * this mutator name).
 */
const arrayAssociativeMutator: SideEffectMutator = (entity, sideEffect) => {
  if (entity.type !== "Rectangular Associative Array") return entity;
  if (sideEffect.label !== "Associative") return entity;
  const nextAssociative = sideEffect.raw === true;
  const arr = entity as ArrayRectEntity;
  if (nextAssociative === arr.associative) return entity;
  const displayDeg = resolveArrayAxisAngleDisplayDeg(arr.axisAngle, arr.associative);
  const convertedAxisAngle = arrayAxisAngleDisplayDegToRaw(displayDeg, nextAssociative);
  return { ...arr, associative: nextAssociative, axisAngle: convertedAxisAngle };
};

/**
 * `textInsertionMoveMutator` (GRIPS-DRAG-MORE-TYPES, Codex P2 round 2) — a
 * TEXT "Insertion X"/"Insertion Y" directive is a whole-entity TRANSLATE, so
 * it must ALSO shift `alignmentPoint` (when the TEXT is justified) by the
 * SAME delta — mirroring every other whole-entity transform
 * (`geometryTransforms.ts::transformEntityCoords`'s "Text" case:
 * `insertionPoint`/`alignmentPoint` both move under the same `fn`). Each
 * axis (X, Y) surfaces as its OWN directive within a single apply (a grip
 * drag submits both), so this mutator runs once per axis — reading the
 * CURRENT (already-updated-by-any-prior-directive) `entity.insertionPoint`
 * as the "old" value for the delta, which composes correctly across the two
 * sequential calls (mirrors every other coupled-field mutator's contract:
 * pure, derives from the entity it is handed).
 *
 * DELIBERATELY the coupling site instead of making "Alignment X"/
 * "Alignment Y" independently owned/editable (a round-1 attempt at that was
 * reverted — see `EXTENDED_GEOMETRY_OPTIONAL_BASE_POINT_COORDS`'s doc in
 * typedPropertyWriter.ts): `alignmentPoint` is NEVER itself a submitted
 * label through this path, so the shell-mirror "can't tell absent from
 * coincidentally-equal" gap that made independent ownership unsafe can never
 * be reached. Guards a non-Text entity / non-Insertion label (defensive:
 * only Text's Insertion X/Y descriptors declare this mutator name — MText
 * has no `alignmentPoint` and does not route its Insertion X/Y through it).
 */
const textInsertionMoveMutator: SideEffectMutator = (entity, sideEffect) => {
  if (entity.type !== "Text") return entity;
  const axis: "x" | "y" | undefined =
    sideEffect.label === "Insertion X"
      ? "x"
      : sideEffect.label === "Insertion Y"
        ? "y"
        : undefined;
  if (!axis) return entity;
  // P2 empty-clear guard (mirrors `parseTypedPropertyInput`'s point-coord
  // reject, typedPropertyWriterCore.ts) — side-effect routing happens
  // BEFORE that shared gate (`applyTypedPropertyEditWithDescriptor` step 2
  // precedes step 3+4's `parseTypedPropertyInput`), and `defaultParse`
  // coerces an empty/whitespace display to a finite `0` (`Number("") ===
  // 0`), which is NOT NaN and would otherwise sail past the finite check
  // below and silently relocate the text to X=0 / Y=0 on a cleared cell. A
  // side-effect mutator owns its OWN value validation (no generic gate
  // covers it), so this is CHECKED HERE, not assumed upstream.
  if (sideEffect.display.trim() === "") return entity;
  const newValue = typeof sideEffect.raw === "number" ? sideEffect.raw : Number(sideEffect.raw);
  if (!Number.isFinite(newValue)) return entity;
  const textEntity = entity as TextEntity;
  const oldValue = textEntity.insertionPoint[axis];
  const delta = newValue - oldValue;
  const insertionPoint = { ...textEntity.insertionPoint, [axis]: newValue };
  const alignmentPoint = textEntity.alignmentPoint
    ? { ...textEntity.alignmentPoint, [axis]: textEntity.alignmentPoint[axis] + delta }
    : textEntity.alignmentPoint;
  return { ...textEntity, insertionPoint, alignmentPoint };
};

/**
 * VALUE-level rejection for the numeric Viewport VIEW side-effect fields — the ONE
 * TRUTH both the `viewportMutator` guard AND the shell-mirror
 * (`typedExtendedGeometryShellRejects` via {@link sideEffectValueRejects}) consult,
 * so the shell preview never accepts a value the document mutator refuses. `View
 * height` (group 45) is the zoom magnification — STRICTLY POSITIVE (parity with the
 * command's `viewHeight.positive()` schema; blank / NaN / ≤0 → reject). `View center
 * X/Y` are PAN coordinates that legitimately accept 0 / negative — only blank / NaN
 * reject. `Display lock` (boolean) has a total parse — never rejected here.
 */
function viewportViewValueRejects(label: string, display: string): boolean {
  const trimmed = display.trim();
  const n = Number(trimmed);
  if (label === "View height") {
    return trimmed === "" || !Number.isFinite(n) || !(n > 0);
  }
  if (label === "View center X" || label === "View center Y") {
    return trimmed === "" || !Number.isFinite(n);
  }
  return false;
}

/**
 * DEFUSE S15 Slice F (@codex P2-3) — VALUE-level reject verdict for a registered
 * side-effect mutator, independent of the entity (format / range only). The
 * shell-mirror (`typedExtendedGeometryShellRejects`) consults this for SIDE-EFFECT
 * owned labels so the preview / audit bag never shows a value the DrawingDocument
 * mutator would refuse — the shell↔document divergence this cutover removes,
 * extended from the plain-owned labels to the side-effect ones. The verdict is the
 * mutator's OWN validation (one truth): `viewportScaleMutator` → the shared
 * `parseViewportScaleFraction` positivity gate (Scale 0:N / non-positive → reject);
 * `viewportMutator` → `viewportViewValueRejects` (View height ≤0 → reject). The
 * Hatch / Spline side-effects have NO value-level reject — an empty Pattern / a
 * malformed Fit-point list are PRESERVE no-ops the shell's own count-lock validators
 * (`isWellFormedCoordinateProperty`) already own — so they return `false` (the
 * mechanism is general; only the viewport mutators declare a value-reject).
 *
 * GRIPS-DRAG-MORE-TYPES round 2 — `textInsertionMoveMutator` (Text Insertion
 * X/Y) also declares one: an empty/whitespace submission REJECTS (mirrors the
 * mutator's OWN `sideEffect.display.trim() === ""` guard — the shell must
 * agree, or a cleared Insertion X cell would show blank in the panel while
 * the document silently keeps its old value, a shell↔document display
 * desync in the OTHER direction from the corruption class this function
 * exists to prevent). A non-finite submission (a non-numeric string such as
 * "abc") ALSO REJECTS (local Codex review, round 3) — the mutator's own
 * `Number.isFinite(newValue)` guard would silently no-op the DOCUMENT write,
 * but without this mirror the SHELL preview bag would still show the
 * literal "abc" (or whatever finite-looking coercion the shell's generic
 * point-coord path produces) for a value the document never persisted —
 * the exact shell/document divergence class this function exists to close.
 */
export function sideEffectValueRejects(
  mutatorName: string,
  label: string,
  value: string,
): boolean {
  switch (mutatorName) {
    case "viewportScaleMutator":
      return label === "Scale" ? parseViewportScaleFraction(value) === null : false;
    case "viewportMutator":
      return viewportViewValueRejects(label, value);
    case "textInsertionMoveMutator": {
      if (label !== "Insertion X" && label !== "Insertion Y") return false;
      const trimmed = value.trim();
      return trimmed === "" || !Number.isFinite(Number(trimmed));
    }
    default:
      return false;
  }
}

/**
 * `sideEffectCanonicalizes` — the SIDE-EFFECT sibling of the plain-owned
 * `typedExtendedGeometryShellCanonicalizes` CANONICALIZING-no-op verdict
 * (Codex P2, local review round 7 on the array-spec-render-arm slice):
 * `arrayAssociativeMutator` moving "Associative" from the plain owned set to
 * the side-effect owned set (round 5) meant the shell mirror STOPPED
 * consulting it for canonicalization at all — a same-value ALTERNATE token
 * submit (document shows "Yes", the panel/API resubmits "true") is correctly
 * a document NO-OP (the mutator's own `nextAssociative === arr.associative`
 * guard), but the shell would then store the literal "true" in the preview
 * bag, diverging from the document's still-"Yes" reality.
 *
 * TRUE per-mutator opt-in (mirrors `sideEffectValueRejects`'s switch shape),
 * NOT a blanket generic toRaw-equality check for every side-effect field:
 * a mutator's no-op condition can be MORE than raw-value equality of the
 * single submitted field (e.g. `hatchPatternMutator`'s no-op depends on the
 * DERIVED coupled-field diff, not just the Pattern string; `viewportScale
 * Mutator`'s raw is a LOSSY ratio projection, per the DISPLAY-LABEL-compare
 * note above — a naive raw-equality canonicalization there would be WRONG).
 * `arrayAssociativeMutator`'s no-op is PROVABLY the plain boolean-raw
 * equality (`sideEffect.raw === entity.associative`, and the current
 * DISPLAY's own toRaw parse already recovers `entity.associative` exactly —
 * "Yes"/"No" round-trip losslessly through the truthy-token toRaw), so it is
 * the one mutator opted in here; every other mutator returns `false`
 * (unchanged pre-round-7 behaviour) until individually verified safe.
 *
 * GRIPS-DRAG-MORE-TYPES round 4 (local Codex review) — `textInsertionMoveMutator`
 * is ALSO opted in: its no-op gate (`buildTypedExtendedGeometrySideEffectPatch`'s
 * `directiveIsNoop`) is the SAME generic `isTypedFieldEditNoop` single-field
 * raw-equality check every plain owned label uses (Text's Insertion X/Y are
 * NOT in `LABEL_CANONICAL_MUTATORS`, so they never take the lossy
 * label-canonical branch) — provably safe by the same reasoning as
 * `arrayAssociativeMutator`. Without this, a canonicalizing re-submit (current
 * document/panel "1", submit "1.0" — same NUMBER, different STRING) would
 * document-no-op (the mutator never runs, insertionPoint/alignmentPoint stay
 * untouched) while the shell preview bag stored the literal "1.0" — a phantom
 * value the document/DXF export never persists, the exact display↔document
 * desync class `sideEffectCanonicalizes` exists to close.
 */
export function sideEffectCanonicalizes(mutatorName: string): boolean {
  switch (mutatorName) {
    case "arrayAssociativeMutator":
    case "textInsertionMoveMutator":
      return true;
    default:
      return false;
  }
}

/**
 * DEFUSE S15 Slice F (@codex P2-4) — mutators whose descriptor RAW is a LOSSY
 * projection of the DISPLAY, so the CHANGED-VALUE no-op guard must compare the
 * DISPLAY LABEL, not the raw. `viewportScaleMutator` is the sole case: the Scale
 * descriptor `read`/`toRaw` are the numeric RATIO (`height/viewHeight`), but the
 * mutator consumes the `num:denom` DISPLAY LABEL to write `vpOverrideScale` +
 * `viewportCannoscaleName`. `1:100` and `2:200` share a ratio yet are DIFFERENT
 * scales, so the generic `isTypedFieldEditNoop` (which falls back to `rawEqual` on
 * the ratio) wrongly drops a ratio-equivalent relabel and the exact custom rational
 * never persists. The side-effect builder compares `resolveDisplay === value` for
 * these mutators. Every other side-effect round-trips its raw faithfully (Pattern /
 * Fit points structural, View numerics, Display-lock boolean) so it keeps the
 * generic guard with its raw-precision-preserve backstop.
 */
export const LABEL_CANONICAL_MUTATORS: ReadonlySet<string> = new Set<string>([
  "viewportScaleMutator",
]);

/**
 * DEFUSE S15 Slice F (@codex round-6 P2-1) — the CHANGED-VALUE no-op verdict for a
 * LABEL-CANONICAL side-effect directive, computed against the EXACT stored carrier
 * rather than the lossy derived display. `viewportScaleMutator` writes the exact
 * `vpOverrideScale` {num,denom} + `viewportCannoscaleName` "num:denom", so a
 * submitted Scale label is a genuine no-op IFF its parsed rational equals THAT
 * stored carrier ({@link viewportStoredScaleFraction}):
 *   - `2:200`→`1:100` (ratio-equal, DIFFERENT carrier) is a CHANGE — the old
 *     `resolveDisplay` compare returned "1:100" for BOTH and wrongly dropped the
 *     relabel, leaving the stored `2:200` XDATA/typed scale stuck;
 *   - `1:100`→`1:100` is a real no-op (rational equal);
 *   - a viewport with NO exact carrier (a purely derived-ratio scale) has no
 *     rational to compare against → fall back to `derivedDisplay()` equality;
 *   - an UNPARSEABLE submitted label → no-op (the mutator no-ops it anyway, so the
 *     directive is dropped rather than surfaced).
 * Any OTHER (future) label-canonical mutator keeps the derived-display compare. The
 * `derivedDisplay` thunk is `resolveDisplay(descriptor, entity)` supplied by the
 * writer (keeping the descriptor/display dependency out of this leaf module).
 */
export function labelCanonicalDirectiveIsNoop(
  mutatorName: string,
  entity: DrawingEntity,
  value: string,
  derivedDisplay: () => string,
): boolean {
  if (mutatorName === "viewportScaleMutator" && entity.type === "Viewport") {
    const submitted = parseViewportScaleFraction(value);
    if (!submitted) return true; // unparseable → the mutator no-ops → drop the directive
    const stored = viewportStoredScaleFraction(entity as ViewportEntity);
    if (stored) {
      return submitted.num === stored.num && submitted.denom === stored.denom;
    }
    return derivedDisplay() === value;
  }
  return derivedDisplay() === value;
}

/**
 * NAME → pure mutator. EVERY `constraints.sideEffects` name a CUTOVER kind routes
 * MUST have an entry here (pinned by `sideEffectMutators.census.test.ts`). The
 * declared names are `hatchPatternMutator` (Hatch Pattern — REGISTERED Slice E),
 * `splineFitRecomputeMutator` (Spline Fit points — REGISTERED Slice D),
 * `viewportMutator` (Viewport Display lock / View height / View center X/Y) +
 * `viewportScaleMutator` (Viewport Scale) — REGISTERED Slice F —
 * `arrayAssociativeMutator` (ArrayRect Associative — REGISTERED array-spec-
 * render-arm, Codex P2 round 5: converts the coupled `axisAngle` unit),
 * `textInsertionMoveMutator` (Text Insertion X/Y — REGISTERED
 * GRIPS-DRAG-MORE-TYPES round 2: shifts `alignmentPoint` in lockstep with a
 * whole-entity insertion move) — and
 * `blockHyperlinkMutator` (Block Hyperlink — DELIBERATELY absent. Block IS a
 * cutover kind as of Slice G, but its Hyperlink label is DEFERRED, not routed:
 * the unregistered-mutator gate in `getTypedExtendedGeometrySideEffectLabels`
 * excludes any label whose mutator is not registered, so `Block` OWNS zero
 * side-effect labels and Hyperlink stays on the legacy path — proven never-routed
 * by the census's NOT_YET_ROUTED allowlist + the owned-set assertion). The
 * viewport mutators DELEGATE
 * to the shared `viewportMutateEntity` helpers the dedicated `applyViewport*`
 * commands also call, so panel + command produce identical entity state (one truth).
 */
export const SIDE_EFFECT_MUTATOR_REGISTRY: Record<string, SideEffectMutator> = {
  hatchPatternMutator,
  splineFitRecomputeMutator,
  viewportMutator,
  viewportScaleMutator,
  arrayAssociativeMutator,
  textInsertionMoveMutator,
};

/** Is `name` a registered side-effect mutator? (registry-membership predicate the
 *  side-effect owned-label derivation consults so an UNIMPLEMENTED mutator's
 *  descriptor field is never advertised as writer-owned). */
export function isSideEffectMutatorRegistered(name: string): boolean {
  return Object.prototype.hasOwnProperty.call(SIDE_EFFECT_MUTATOR_REGISTRY, name);
}

/** The registered mutator names (census / introspection). */
export function registeredSideEffectMutatorNames(): string[] {
  return Object.keys(SIDE_EFFECT_MUTATOR_REGISTRY);
}

/**
 * Apply ONE writer-surfaced side-effect directive through its registered mutator.
 * LOUD FAIL: an UNREGISTERED mutator name THROWS — the by-construction guard
 * against the pre-PIVOT silent-no-op regression (a side-effect field that reaches
 * the consumer with no mutator is a bug, not a no-op). `context` carries the
 * cross-field CHANGED-sibling verdict a coupled-field mutator gates on (default
 * EMPTY for a single-directive caller / the census). Pure — returns the mutated
 * entity, never mutates `entity`.
 */
export function applyTypedSideEffect(
  entity: DrawingEntity,
  sideEffect: TypedPropertySideEffect,
  context: SideEffectMutatorContext = EMPTY_SIDE_EFFECT_CONTEXT,
): DrawingEntity {
  const mutator = SIDE_EFFECT_MUTATOR_REGISTRY[sideEffect.mutator];
  if (!mutator) {
    throw new Error(
      `typed side-effect mutator "${sideEffect.mutator}" (label "${sideEffect.label}") ` +
        `is not registered in SIDE_EFFECT_MUTATOR_REGISTRY — a side-effect descriptor ` +
        `field reached the reducer with no mutator (register it, or exclude its ` +
        `descriptor field from a cutover kind)`,
    );
  }
  return mutator(entity, sideEffect, context);
}
